// Library methods
import { forwardRef, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

// MUI Components
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";

// Components
import ClinicSwitcher from "../ClinicSwitcher";
import UserMenuWrapper from "../UserMenuWrapper";
import NavbarTextButton from "./NavbarTextButton";
import TabSwitcher from "../TabSwitcher";
import NewAppVersionWarningPopup from "../NewAppVersionWarningPopup";
import DeviceUpdateInstructionPopup from "../DeviceUpdateInstructionPopup";
import { QuestionCircleIcon } from "../../../assets/icons";

// Assets
import eyeLogo from "../../../assets/images/logos/eyelogo.svg";
import { DeviceAppVersionContext } from "../../../contexts/DeviceContext";

const Navbar = forwardRef(
  ({ canReachPages = true, clickableLogo = true }, ref) => {
    // internationalization
    const { t } = useTranslation();

    const location = useLocation();
    const navigate = useNavigate();

    // device app version
    const { newAppVersionWarningPopup } = useContext(DeviceAppVersionContext);

    const openManual = () => {
      navigate("/operating-manual");
    };

    const navbarPages = [
      {
        dataTestId: "navbar-dashboard-button",
        title: t("tab_dashboard"),
        path: "/dashboard",
      },
      {
        dataTestId: "navbar-patients-button",
        title: t("tab_patients"),
        path: "/patients",
      },
    ];

    return (
      <>
        <Box id="navbar-container" zIndex={99}>
          <AppBar
            ref={ref}
            position="relative"
            elevation={0}
            sx={{
              backgroundColor: "#202338",
              color: "#282c3c",
              height: { xs: 42, sm: 85 },
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Toolbar
              disableGutters
              sx={{ display: "flex", justifyContent: "space-between" }}
            >
              <Box
                component="img"
                sx={{
                  flexGrow: 1,
                  maxWidth: { xs: 47, sm: 73 },
                  mx: { xs: 3, md: 5, lg: 5 },
                  my: 3,
                  display: { sm: "flex", md: "flex" },
                  ...(clickableLogo ? { cursor: "pointer" } : {}),
                }}
                alt="RetinaLogik Logo"
                src={eyeLogo}
                {...(clickableLogo ? { onClick: () => navigate("/") } : {})}
              />
              <Box
                sx={{
                  flexGrow: 1,
                  display: { xs: "none", sm: "flex", md: "flex" },
                }}
              >
                {canReachPages &&
                  navbarPages.map((page) => (
                    <NavbarTextButton
                      key={page.title}
                      dataTestId={page.dataTestId}
                      title={page.title}
                      onClick={() => navigate(page.path)}
                      isUnderlined={location.pathname.startsWith(page.path)}
                    />
                  ))}
              </Box>
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexGrow: 1,
                  justifyContent: "flex-end",
                }}
                mr={2}
              >
                <Box py={{ xs: 1 }} display={"flex"} alignItems={"center"}>
                  <ClinicSwitcher />

                  {/* Help icon button */}
                  <Button
                    data-testid="navbar-help-button-icon"
                    onClick={openManual}
                    sx={{
                      position: "relative",
                      display: { xs: "flex", sm: "none" },
                      alignContent: "center",
                      minWidth: { xs: 0, sm: 64 },
                      ml: { md: 2, lg: 4 },
                      color: "#E7EBF8",
                      fontSize: 20,
                    }}
                  >
                    <QuestionCircleIcon aria-label={t("word_help")} />
                  </Button>
                  {/* Help text button */}
                  <Box sx={{ display: { xs: "none", sm: "block" } }}>
                    <NavbarTextButton
                      dataTestId="navbar-help-button"
                      title={t("word_help")}
                      onClick={openManual}
                      isUnderlined={location.pathname.startsWith(
                        "/operating-manual"
                      )}
                      customStyles={{ fontSize: 16 }}
                      leftIcon={
                        <QuestionCircleIcon
                          color="#E7EBF8"
                          aria-label={t("word_help")}
                        />
                      }
                    />
                  </Box>

                  <UserMenuWrapper canReachPages={canReachPages} />
                  <Box
                    sx={{
                      flexGrow: 1,
                      display: { xs: "flex", sm: "none", md: "none" },
                    }}
                  >
                    <TabSwitcher
                      navbarPages={navbarPages}
                      location={location}
                    />
                  </Box>
                </Box>
              </Box>
            </Toolbar>
          </AppBar>
          {newAppVersionWarningPopup && <NewAppVersionWarningPopup />}
        </Box>
        <DeviceUpdateInstructionPopup />
      </>
    );
  }
);
export default Navbar;
